/**
 * Styled primitives for the Resume Builder — same dark theme as the Agreements page.
 */
import styled from 'styled-components';

export const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 5000;
  background: rgba(6, 9, 15, 0.72); backdrop-filter: blur(3px);
  display: flex; align-items: stretch; justify-content: center;
  padding: 18px;
`;

export const Shell = styled.div`
  width: 100%; max-width: 1500px; display: flex; flex-direction: column;
  background: linear-gradient(180deg, #0f1419 0%, #121826 100%);
  border: 1px solid rgba(120, 140, 180, 0.22); border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5); overflow: hidden; color: #e6e9f0;
`;

export const TopBar = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 14px 18px; border-bottom: 1px solid rgba(120, 140, 180, 0.15);
`;

export const Title = styled.h2`
  margin: 0; font-size: 18px; color: #fff; display: flex; align-items: center; gap: 10px;
`;

export const Status = styled.span`
  font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 999px;
  color: ${p => (p.$dirty ? '#fcd34d' : '#86efac')};
  background: ${p => (p.$dirty ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)')};
`;

export const Spacer = styled.div`flex: 1;`;

export const Body = styled.div`
  flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  @media (max-width: 1100px) { grid-template-columns: 1fr; overflow-y: auto; }
`;

export const EditorPane = styled.div`
  overflow-y: auto; padding: 18px; border-right: 1px solid rgba(120, 140, 180, 0.12);
  @media (max-width: 1100px) { overflow: visible; border-right: none; }
`;

export const PreviewPane = styled.div`
  display: flex; flex-direction: column; padding: 18px; gap: 10px; min-height: 0;
  @media (max-width: 1100px) { min-height: 80vh; }
`;

export const Iframe = styled.iframe`
  flex: 1; width: 100%; min-height: 400px; border: none; border-radius: 10px; background: #fff;
`;

export const PreviewEmpty = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center; text-align: center;
  border: 1px dashed rgba(120, 140, 180, 0.3); border-radius: 10px; color: #8b93a7; font-size: 14px; padding: 20px;
`;

export const Card = styled.div`
  background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(120, 140, 180, 0.15);
  border-radius: 12px; margin-bottom: 14px;
`;

export const CardHead = styled.button`
  width: 100%; display: flex; align-items: center; gap: 10px; padding: 13px 16px;
  background: none; border: none; color: #fff; cursor: pointer; text-align: left;
  font-size: 14.5px; font-weight: 700;
  .count { font-size: 12px; font-weight: 600; color: #8b93a7; }
  .chev { margin-left: auto; color: #8b93a7; transition: transform .2s; transform: rotate(${p => (p.$open ? 90 : 0)}deg); }
`;

export const CardBody = styled.div`padding: 0 16px 16px;`;

export const Entry = styled.div`
  border: 1px solid rgba(120, 140, 180, 0.14); border-radius: 10px; padding: 12px; margin-bottom: 10px;
  background: rgba(10, 14, 22, 0.35);
`;

export const EntryHead = styled.div`
  display: flex; align-items: center; gap: 6px; margin-bottom: 10px;
  .label { font-size: 13px; font-weight: 700; color: #c7cee0; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

export const Row = styled.div`display: flex; gap: 10px; flex-wrap: wrap; ${p => p.$mt && 'margin-top: 10px;'}`;
export const Field = styled.div`flex: ${p => p.$grow || 1}; min-width: ${p => p.$min || '160px'}; display: flex; flex-direction: column; gap: 5px;`;
export const Label = styled.label`font-size: 11.5px; color: #9aa3ba; font-weight: 600;`;
export const Hint = styled.div`font-size: 12px; color: #8b93a7; line-height: 1.45; ${p => p.$mb && 'margin-bottom: 10px;'}`;

const inputCss = `
  background: rgba(10,14,22,0.7); border: 1px solid rgba(120,140,180,0.2); border-radius: 8px;
  padding: 8px 10px; color: #eef1f7; font-size: 13.5px; outline: none; width: 100%; box-sizing: border-box;
  &:focus { border-color: rgba(139,92,246,0.6); }
  &:disabled { opacity: 0.45; }
`;
export const Input = styled.input`${inputCss}`;
export const TextArea = styled.textarea`${inputCss} min-height: ${p => p.$h || 80}px; resize: vertical; font-family: inherit; line-height: 1.45;`;
export const Select = styled.select`
  ${inputCss} cursor: pointer; appearance: none; -webkit-appearance: none; padding-right: 30px;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239aa3ba' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'/></svg>");
  background-repeat: no-repeat; background-position: right 10px center;
  & option { background: #121826; color: #eef1f7; }
`;

export const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; white-space: nowrap;
  padding: ${p => (p.$sm ? '6px 11px' : '9px 15px')}; border-radius: 9px; cursor: pointer;
  font-weight: 600; font-size: ${p => (p.$sm ? '12.5px' : '13.5px')}; border: none; color: #fff;
  background: ${p => p.$variant === 'ghost' ? 'rgba(255,255,255,0.06)'
    : p.$variant === 'danger' ? 'linear-gradient(135deg,#ef4444,#dc2626)'
    : p.$variant === 'ai' ? 'linear-gradient(135deg,#0ea5e9,#6366f1)'
    : p.$variant === 'green' ? 'linear-gradient(135deg,#10b981,#16a34a)'
    : 'linear-gradient(135deg,#6366f1,#8b5cf6)'};
  ${p => p.$variant === 'ghost' && 'border: 1px solid rgba(120,140,180,0.22); color: #c7cee0;'}
  &:hover:not(:disabled) { filter: brightness(1.1); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  svg { width: 15px; height: 15px; }
`;

export const IconBtn = styled.button`
  width: 28px; height: 28px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  border-radius: 7px; border: 1px solid rgba(120,140,180,0.18); background: rgba(255,255,255,0.04);
  color: ${p => (p.$danger ? '#fca5a5' : '#aeb6ca')}; cursor: pointer; font-size: 14px; line-height: 1;
  &:hover:not(:disabled) { background: ${p => (p.$danger ? 'rgba(239,68,68,0.18)' : 'rgba(139,92,246,0.16)')}; }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

export const Chip = styled.span`
  display: inline-flex; align-items: center; gap: 6px; padding: 4px 6px 4px 10px; border-radius: 999px;
  background: rgba(139,92,246,0.15); border: 1px solid rgba(139,92,246,0.35); color: #ddd6fe; font-size: 12.5px;
  button { background: none; border: none; color: #c4b5fd; cursor: pointer; font-size: 14px; line-height: 1; padding: 0 2px; }
`;

export const Banner = styled.div`
  border-radius: 10px; padding: 11px 14px; margin-bottom: 14px; font-size: 13px; line-height: 1.5;
  border: 1px solid ${p => (p.$tone === 'warn' ? 'rgba(245,158,11,0.4)' : 'rgba(59,130,246,0.35)')};
  background: ${p => (p.$tone === 'warn' ? 'rgba(245,158,11,0.08)' : 'rgba(59,130,246,0.08)')};
  color: ${p => (p.$tone === 'warn' ? '#fcd982' : '#bfdbfe')};
  ul { margin: 6px 0 0; padding-left: 18px; }
  li { margin: 2px 0; }
`;

export const Check = styled.label`
  display: inline-flex; align-items: center; gap: 7px; font-size: 13px; color: #c7cee0; cursor: pointer; user-select: none;
  input { accent-color: #8b5cf6; width: 15px; height: 15px; }
`;

export const HiddenFile = styled.input`display: none;`;
export const FileLabel = styled.label`
  display: inline-block; padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600;
  color: #c7cee0; background: rgba(255,255,255,0.06); border: 1px solid rgba(120,140,180,0.25);
  &:hover { background: rgba(139,92,246,0.14); border-color: rgba(139,92,246,0.5); }
`;
